import { colors } from '@dexma/ui-components';
import { useCommonDependencies } from 'adapter/in/common/commonDependencies';
import {
    ConfigFieldType,
    FieldStatus,
    SelectorFieldDataImpl,
    SelectorOption,
    SelectorType,
    TreeSelectorOption,
} from 'adapter/in/common/form/types';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { isSome } from 'fp-ts/lib/Option';
import { useEffect, useState } from 'react';
import { PresetFieldDependency, ReadPresetFieldCriteriaBuilder } from 'use-case/presets/fields/read/readPresetFieldCriteria';
import { useCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import SelectorValue from 'domain/entity/preset/field/selectorValue';
import { parsePeriodCodeToPeriodTemplateState } from 'adapter/in/common/components/PeriodSelector/periodSelectorUtils';
import { buildSelectorFieldData } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import { isSelectorValueValid } from 'adapter/in/common/form/Selector';

type SelectorOptionWithChildren = SelectorOption & {
    children?: SelectorOptionWithChildren[];
};

const hasChildren = (option: SelectorOption): option is SelectorOptionWithChildren =>
    'children' in option &&
    Array.isArray((option as SelectorOptionWithChildren).children) &&
    ((option as SelectorOptionWithChildren).children?.length ?? 0) > 0;

const hasSelectorValueChildren = (option: SelectorValue): boolean => Array.isArray(option.children) && option.children.length > 0;

const siblingGroupHasOnlyStructuralNodes = (siblings: SelectorValue[]): boolean =>
    siblings.length > 0 && siblings.every(hasSelectorValueChildren);

const isDisabledSelectorValue = (option: SelectorValue): boolean =>
    option.disabled === true || option.properties?.disabled === true;

const getDisabledTreeProps = (option: SelectorValue): Pick<SelectorOption, 'disabled'> =>
    isDisabledSelectorValue(option) ? { disabled: true } : {};

const isStructuralTreeOption = (option: SelectorOption): boolean => option.type === SelectorType.TREE && hasChildren(option);

const isDisabledTreeOption = (option: SelectorOption): boolean => option.type === SelectorType.TREE && option.disabled === true;

const getDefaultValues = (options: SelectorOption[], minSelectionLength?: number) => {
    if (minSelectionLength !== undefined && minSelectionLength === 0) return [];
    const selectableOptions = flattenSelectorOptions(options).filter(isSelectableOption);
    const selectedValues = selectableOptions.filter((option) => option.selected);
    return selectedValues.length > 0 ? selectedValues : selectableOptions.slice(0, 1);
};

export const getInitialSelectorValues = (value: SelectorFieldDataImpl, options: SelectorOption[]) =>
    value.getInitialValue() !== undefined
        ? flattenSelectorOptions(options).filter((option) => value.getInitialValue().includes(option.value))
        : [];

export const flattenSelectorOptions = (options: SelectorOption[]): SelectorOption[] =>
    options.flatMap((option) => [option, ...(hasChildren(option) ? flattenSelectorOptions(option.children ?? []) : [])]);

export const isSelectableOption = (option: SelectorOption): boolean =>
    option.selectable !== false &&
    !isDisabledTreeOption(option) &&
    (option.selectable === true || !isStructuralTreeOption(option));

const getTreeSelectionProps = (
    option: SelectorValue,
    siblingOptions: SelectorValue[]
): Pick<SelectorOption, 'checkable' | 'disableCheckbox' | 'disabled' | 'selectable'> => {
    if (hasSelectorValueChildren(option)) {
        if (option.selectable === true) {
            return {
                ...getDisabledTreeProps(option),
                selectable: true,
            };
        }

        return siblingGroupHasOnlyStructuralNodes(siblingOptions)
            ? {
                  ...getDisabledTreeProps(option),
                  checkable: false,
                  selectable: false,
              }
            : {
                  ...getDisabledTreeProps(option),
                  disableCheckbox: true,
                  selectable: false,
              };
    }

    if (option.selectable === false) {
        return {
            ...getDisabledTreeProps(option),
            disableCheckbox: true,
            selectable: false,
        };
    }

    return {
        ...getDisabledTreeProps(option),
        selectable: option.selectable,
    };
};

const getSelectionProps = (
    option: SelectorValue,
    type: SelectorType,
    siblingOptions: SelectorValue[]
): Pick<SelectorOption, 'checkable' | 'disableCheckbox' | 'disabled' | 'selectable'> =>
    type === SelectorType.TREE
        ? getTreeSelectionProps(option, siblingOptions)
        : {
              disabled: option.properties?.disabled,
              selectable: option.selectable,
          };

const getOptionColor = (type: SelectorType, isDisabled?: boolean): string => {
    if (type === SelectorType.TREE) {
        return isDisabled ? colors.gray300 : colors.black;
    }
    return colors.gray300;
}

export function mapSelectorValuesToOptions(options: SelectorValue[], type: SelectorType): SelectorOption[] {
    return options.map((option) => mapSelectorValueToOption(option, type, options));
}

export function mapSelectorValueToOption(
    option: SelectorValue,
    type: SelectorType,
    siblingOptions: SelectorValue[] = [option]
): SelectorOption {
    const label =
        option.properties && option.properties.shortLabel !== '' && option.properties.shortLabel !== undefined
            ? option.properties.shortLabel
            : option.label;
    const children = hasSelectorValueChildren(option) ? option.children ?? [] : [];
    const selectionProps = getSelectionProps(option, type, siblingOptions);
    const mappedOption: SelectorOption = {
        type: type === SelectorType.TREE ? SelectorType.TREE : SelectorType.SELECT,
        value: option.id,
        label,
        key: option.id,
        title: label,
        id: option.id,
        color: getOptionColor(type, selectionProps.disabled),
        tooltip: option.label,
        icon: option.properties ? option.properties.icon : undefined,
        isDisabled: option.properties ? option.properties.isDisabled : undefined,
        ...selectionProps,
        selected: option.selected,
        state: type === SelectorType.PERIOD ? parsePeriodCodeToPeriodTemplateState(option.id) : undefined,
    };

    return children.length > 0
        ? ({
              ...mappedOption,
              children: mapSelectorValuesToOptions(children, type) as TreeSelectorOption[],
          } as TreeSelectorOption)
        : mappedOption;
}

export const useBaseDashboardsApiSelector = <POption>(
    name: string,
    type: SelectorType,
    configType: ConfigFieldType,
    pageSize: number,
    value: SelectorFieldDataImpl,
    shouldLoad: boolean,
    dependantValues: PresetFieldDependency[],
    onChange: (value: SelectorFieldDataImpl) => void
) => {
    const { preset, widgetType } = useCurrentWidgetConfiguration();
    const { readPresetField: readPresetFieldUseCase } = useCommonDependencies();
    const { isLoading, data: presetFieldResult, execute: readPresetField } = useCase(readPresetFieldUseCase, false);
    const [options, setOptions] = useState<POption[]>([]);
    const shouldLoadOptions =
        value && [FieldStatus.INIT, FieldStatus.INITIAL_VALUE, FieldStatus.INVALID, FieldStatus.RESET].includes(value.status) && shouldLoad;
    const hasInitialValue = () => {
        let initialValue = value.getInitialValue()[0];
        if (Array.isArray(initialValue)) {
            initialValue = initialValue[0];
        }
        return (
            (value.status === FieldStatus.INITIAL_VALUE ||
                value.status === FieldStatus.DEFAULT_DATA ||
                value.status === FieldStatus.RESET) &&
            !!initialValue
        );
    };
    useEffect(() => {
        if (shouldLoadOptions) {
            readPresetField(
                ReadPresetFieldCriteriaBuilder.create()
                    .withPreset(preset.key)
                    .withDependencies(dependantValues.map((dep) => new PresetFieldDependency(dep.key.split('-')[0], dep.value)))
                    .withWidgetType(widgetType)
                    .withField(name.split('-')[0])
                    .withPageSize(pageSize ?? 0)
                    .build()
            );
        }
    }, [shouldLoadOptions]);

    useEffect(() => {
        if (isSome(presetFieldResult)) {
            const optionsToSet = mapSelectorValuesToOptions(presetFieldResult.value.values as SelectorValue[], type);
            setOptions((optionsToSet as unknown) as POption[]);
            if (optionsToSet.length === 0 && presetFieldResult.value.minSelectionLength && presetFieldResult.value.minSelectionLength > 0) {
                onChange(buildSelectorFieldData(FieldStatus.INVALID, value.initialValue, configType, []));
            } else {
                switch (type) {
                    case SelectorType.PERIOD: {
                        const foundValue = optionsToSet.find((v) => v.selected);
                        const initialDefaultValue =
                            hasInitialValue() &&
                            [FieldStatus.HAS_VALUE, FieldStatus.INITIAL_VALUE, FieldStatus.RESET].includes(value.status)
                                ? (value.getInitialValue() as Array<string>)[0]
                                : foundValue?.id || presetFieldResult.value.values[0].id;

                        onChange(
                            buildSelectorFieldData(FieldStatus.HAS_VALUE, value.initialValue, configType, [
                                {
                                    type: SelectorType.SELECT,
                                    value: initialDefaultValue,
                                    label: initialDefaultValue,
                                    color: initialDefaultValue,
                                    id: initialDefaultValue,
                                },
                            ])
                        );
                        break;
                    }
                    case SelectorType.FIELD_GROUP:
                    case SelectorType.SELECT:
                    default: {
                        const initialValues = getInitialSelectorValues(value, optionsToSet);
                        const selectedValueToSet =
                            initialValues.length > 0
                                ? initialValues
                                : getDefaultValues(optionsToSet, presetFieldResult.value.minSelectionLength);
                        onChange(
                            buildSelectorFieldData(
                                isSelectorValueValid(
                                    presetFieldResult.value.minSelectionLength ?? 1,
                                    presetFieldResult.value.maxSelectionLength ?? 1
                                )(selectedValueToSet)
                                    ? FieldStatus.HAS_VALUE
                                    : FieldStatus.INVALID,
                                selectedValueToSet.map((selValue) => selValue.value).join(','),
                                configType,
                                selectedValueToSet
                            )
                        );
                        break;
                    }
                }
            }
        }
    }, [presetFieldResult]);

    return {
        isLoading,
        options: isSome(presetFieldResult) ? options : [],
        maxSelectionLength:
            isSome(presetFieldResult) && presetFieldResult.value.maxSelectionLength ? presetFieldResult.value.maxSelectionLength : 1,
        minSelectionLength:
            isSome(presetFieldResult) && presetFieldResult.value.minSelectionLength ? presetFieldResult.value.minSelectionLength : 0,
    };
};
